import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { APIClient } from '../helpers/api_helper';
import config from '../config';

/**
 * Custom hook for tracking user activity (page views and clicks)
 */
export const useActivityTracker = () => {
  const location = useLocation();
  const api = useMemo(() => new APIClient(), []);

  // Track page views
  useEffect(() => {
    // Only track if user is authenticated
    const authUser = sessionStorage.getItem('authUser');
    if (authUser) {
      // Debounce to avoid duplicate tracking
      const timer = setTimeout(() => {
        api.create(`${config.api.API_URL}/admin-monitoring/track/pageview`, {
          path: location.pathname
        }).catch(error => {
          // Silently fail - don't interrupt user experience
          console.debug('Failed to track page view:', error);
        });
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [location.pathname, api]);

  // Function to track click events
  const trackClick = (element: string, page?: string) => {
    const authUser = sessionStorage.getItem('authUser');
    if (authUser) {
      api.create(`${config.api.API_URL}/admin-monitoring/track/click`, {
        element,
        page: page || location.pathname
      }).catch(error => {
        // Silently fail
        console.debug('Failed to track click:', error);
      });
    }
  };

  return { trackClick };
};

/**
 * Global click tracker for important buttons
 */
export const initGlobalClickTracking = () => {
  const api = new APIClient();
  
  const clickHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    
    // Check if it's a button or link
    const button = target instanceof Element ? target.closest('button') : null;
    const link = target instanceof Element ? target.closest('a') : null;
    
    if (button && button.classList.contains('btn-primary')) {
      const label = button.textContent?.trim() || 'Unknown Button';
      const authUser = sessionStorage.getItem('authUser');
      
      if (authUser) {
        api.create(`${config.api.API_URL}/admin-monitoring/track/click`, {
          element: `Primary Button: ${label}`,
          page: window.location.pathname
        }).catch(() => {
          // Silently fail
        });
      }
    } else if (link && link.classList.contains('nav-link')) {
      const label = link.textContent?.trim() || 'Unknown Link';
      const authUser = sessionStorage.getItem('authUser');
      
      if (authUser) {
        api.create(`${config.api.API_URL}/admin-monitoring/track/click`, {
          element: `Navigation: ${label}`,
          page: window.location.pathname
        }).catch(() => {
          // Silently fail
        });
      }
    }
  };

  // Track clicks on primary buttons
  document.addEventListener('click', clickHandler);
  
  // Return cleanup function
  return () => {
    document.removeEventListener('click', clickHandler);
  };
};